import * as React from "react";
import {memo} from "react";
import ReactSelect from "react-select";
import {css, withTheme} from "@emotion/react";
import styled from "@emotion/styled";
import {find, isFinite, isFunction, isString} from "lodash";
import {FormErrorMessage} from "@web2/form-fields";
import {IFormFieldProps} from "@web2/form2";

import {ISelectTheme, selectCustomStyles} from "./Styles";
const ReactSelectComponent = ReactSelect;

type ISelectWithTheme = {theme: ISelectTheme};
export type SelectSize = "lg" | "sm";

export interface Option<TValue = string | number | boolean> {
    label?: string;
    value: TValue;
}

interface IProps extends IFormFieldProps<string, string | number | boolean>, ISelectWithTheme {
    label?: string;
    required?: boolean;
    options: Option[];
    clearable?: boolean;
    searchable?: boolean;
    groupClassName?: string;
    className?: string;
    id?: string;
    errorOnBottom?: boolean;
    markable?: boolean;
    refHandler?: (elem: HTMLElement | null) => void;
    onMenuOpen?: () => void;
    onMenuClose?: () => void;
    showPlaceholderAsOption?: boolean;
    defaultMenuIsOpen?: boolean;
    placeholder?: string;
    size?: SelectSize;
}

interface IState {
    triggerOnAfterChange?: boolean;
}

export class SelectC extends React.PureComponent<IProps, IState> {
    public static defaultProps = {
        clearable: false,
        searchable: false,
        ignoreAccents: false,
        markable: false
    };

    private static defaultValue = "";

    constructor(props: IProps) {
        super(props);
        this.state = {triggerOnAfterChange: false};
    }

    public componentDidUpdate(prevProps: IProps) {
        const {name, value, onAfterChange} = this.props;

        if (isFunction(onAfterChange) && this.state.triggerOnAfterChange) {
            this.setState({triggerOnAfterChange: false});
            onAfterChange(name, value);
        }
    }

    /**
     * Callback
     */

    //Package typings allow any as value.
    /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
    private onChange = (label: any, value: any): void => {
        let nextValue = value && value.value;
        nextValue = nextValue == null ? SelectC.defaultValue : nextValue;
        if (isFinite(nextValue) || isString(nextValue)) {
            this.props.onChange(this.props.name, nextValue);
            this.setState({triggerOnAfterChange: true});
        } else {
            //Reset select value if placeholderAsOption was selected
            this.props.onChange(this.props.name, "");
            this.setState({triggerOnAfterChange: true});
        }
    };

    /**
     * Helper
     */

    private findOption = (): Option => {
        const option = find(this.props.options, (o) => o.value === this.props.value);
        return (option == null ? null : option) as Option;
    };

    private customStyles = selectCustomStyles(this.props.theme);

    /**
     * Render
     */

    public render() {
        const {placeholder = "Wybierz", options, showPlaceholderAsOption} = this.props;

        return (
            <SelectWrapper error={this.props.error} errorOnBottom={this.props.errorOnBottom} ref={this.props.refHandler} data-testid="select-wrapper">
                {this.props.label && (
                    <Label htmlFor={this.props.id} data-testid={`select-label-${this.props.name}`}>
                        {this.props.label} {this.props.required && <span data-testid={`select-required-asterisk-${this.props.name}`}>*</span>}
                    </Label>
                )}

                {this.props.error && !this.props.errorOnBottom && <FormErrorMessage error={this.props.error} />}

                <ReactSelectComponent
                    defaultMenuIsOpen={this.props.defaultMenuIsOpen}
                    name={this.props.name}
                    value={this.findOption()}
                    options={showPlaceholderAsOption ? [{value: "", label: placeholder}, ...options] : options}
                    onChange={(value, label) => this.onChange(label, value)}
                    isClearable={this.props.clearable}
                    isSearchable={this.props.searchable}
                    placeholder={placeholder}
                    tabSelectsValue={false}
                    instanceId={this.props.name}
                    id={this.props.id}
                    onMenuOpen={this.props.onMenuOpen}
                    onMenuClose={this.props.onMenuClose}
                    styles={this.customStyles}
                    size={this.props.size}
                    error={this.props.error}
                />

                {this.props.error && this.props.errorOnBottom && <FormErrorMessage error={this.props.error} />}
            </SelectWrapper>
        );
    }
}

export const Select = memo(withTheme(SelectC));

interface ITheme {
    forms: {
        form_group_margin_bottom: string;
        error_message_color: string;
        error_message_input_box_shadow: string;

        label_color: string;
        label_font_weight: number;
        label_font_size: number;
        label_vertical_margin: string;
        label_horizontal_margin: string;
    };
    fonts: {
        line_height_base: string;
    };
}

interface ISelectWrapperThemeProps {
    error?: string[] | null;
    errorOnBottom?: boolean;
}

interface ILabelThemeProps {}

export const SelectWrapper = styled.div<ISelectWrapperThemeProps>`
    margin-bottom: ${(props) => props.theme.forms.form_group_margin_bottom};

    ${(props) =>
        props.error &&
        props.errorOnBottom &&
        css`
            margin-bottom: 0;
        `}
`;

export const Label = styled.label<ILabelThemeProps>`
    text-align: left;
    font-size: ${(props) => props.theme.forms.label_font_size};
    width: 100%;
    margin-bottom: ${(props) => props.theme.forms.label_vertical_margin};
    margin-right: ${(props) => props.theme.forms.label_horizontal_margin};
    font-weight: ${(props) => props.theme.forms.label_font_weight};
    color: ${(props) => props.theme.forms.label_color};
    line-height: ${(props) => props.theme.fonts.line_height_base};
    display: block;
`;
